import type {
  Apis,
  ConfigResponse,
  GetUserContextResponse,
  JSONObject,
  OnMessageEvent,
  RunningContext
} from '@zoom/appssdk';

/**
 * Zoom runs this app in three materially different hosts:
 *
 * - `desktop`: native webview (WebView2 / CEF / WKWebView). Supports the widest API
 *   surface and can run outside a meeting (`inMainClient`).
 * - `web`: browser iframe inside the Zoom Web App. Meeting/webinar contexts only,
 *   a reduced API surface, and partitioned third-party storage.
 * - `mobile` / Zoom Rooms: further reduced surface.
 *
 * Nothing in the app may assume a method exists. Every optional call is guarded by
 * `supports()`, which is derived from the `unsupportedApis` returned by `config`
 * and from `getSupportedJsApis` where available.
 */

/**
 * Capabilities requested at configuration time.
 *
 * Deliberately minimal: every entry is called by this module. Zoom's review process
 * treats unused capabilities as a rejection risk, and each one widens the consent
 * prompt shown to users on install.
 *
 * The app reads only the current participant's meeting-scoped display name, role, and
 * participant UUID. These values are ephemeral and are never sent to the legacy API.
 */
const REQUESTED_CAPABILITIES: Apis[] = [
  // Signed, encrypted context used by the optional legacy API integration.
  'getAppContext',
  'getSupportedJsApis',
  // Meeting-scoped identity and app-to-app messaging. All three support Guest Mode.
  'getUserContext',
  'sendMessage',
  'onMessage',
  // Invitations. Availability varies by client and role, hence the runtime guards.
  'sendAppInvitationToAllParticipants',
  'showAppInvitationDialog',
  // Lifecycle events: the app must resync when Zoom moves or resumes it.
  'onMyUserContextChange',
  'onRunningContextChange',
  'onAppVisibilityChange'
];

export type ZoomProduct = NonNullable<ConfigResponse['product']> | 'unknown';

export type ZoomEnvironment = {
  /** True only when the Zoom Apps SDK configured successfully. */
  embedded: boolean;
  /** Which Zoom host the app is running in. */
  product: ZoomProduct;
  runningContext: RunningContext | 'unknown';
  /** True when the app is inside a meeting or webinar (the only web-client contexts). */
  inMeeting: boolean;
  clientVersion?: string;
  authStatus?: ConfigResponse['auth']['status'];
  /** APIs the running client reported as unavailable. Useful for diagnosing surfaces. */
  unsupportedApis: string[];
  /** APIs available to the current user/role, when the client reports them. */
  supportedApis: string[] | null;
  supports: (api: Apis) => boolean;
};

export const NOT_EMBEDDED: ZoomEnvironment = {
  embedded: false,
  product: 'unknown',
  runningContext: 'unknown',
  inMeeting: false,
  unsupportedApis: [],
  supportedApis: null,
  supports: () => false
};

const MEETING_CONTEXTS: ReadonlySet<string> = new Set([
  'inMeeting',
  'inWebinar',
  'inImmersive',
  'inCollaborate',
  'inCollaborateSidecar',
  'inMeetingChat'
]);

type ZoomSdk = typeof import('@zoom/appssdk').default;

/**
 * The SDK instance is deliberately kept inside a wrapper object.
 *
 * `zoomSdk` is Proxy-based: every property access is dispatched as an API call. Returning
 * it directly from a promise callback makes JavaScript's thenable check read `.then` off
 * the Proxy; the SDK treats that as an unknown API called before `config()` and throws
 * "must call zoomSdk.config before using other API methods". Wrapping it keeps the Proxy
 * away from promise resolution machinery.
 */
type SdkHolder = { sdk: ZoomSdk } | null;

/**
 * Outside Zoom there is no native bridge to answer `config`, and the SDK's own default
 * timeout is 10 seconds. Cap it so a plain browser is not left on a blank screen.
 */
const CONFIG_TIMEOUT_MS = 1200;

let sdkPromise: Promise<SdkHolder> | null = null;
let environment: ZoomEnvironment = NOT_EMBEDDED;

async function loadSdk(): Promise<SdkHolder> {
  if (!sdkPromise) {
    sdkPromise = (async (): Promise<SdkHolder> => {
      try {
        const module = await import('@zoom/appssdk');
        return { sdk: module.default };
      } catch {
        return null;
      }
    })();
  }

  return sdkPromise;
}

function buildEnvironment(response: ConfigResponse, supportedApis?: Apis[]): ZoomEnvironment {
  const unsupported = new Set<string>(response.unsupportedApis ?? []);
  const supported = supportedApis ? new Set<string>(supportedApis) : undefined;

  return {
    embedded: true,
    product: response.product ?? 'unknown',
    runningContext: response.runningContext,
    inMeeting: MEETING_CONTEXTS.has(response.runningContext),
    clientVersion: response.clientVersion,
    authStatus: response.auth?.status,
    unsupportedApis: [...unsupported],
    supportedApis: supportedApis ? [...supportedApis] : null,
    supports: (api: Apis) => {
      if (unsupported.has(api)) return false;
      // `getSupportedJsApis` reflects the current user's role/permissions, which the
      // web client and meeting participants restrict more than the desktop host.
      if (supported) return supported.has(api);
      return true;
    }
  };
}

/**
 * Configures the Zoom Apps SDK. Safe to call in a plain browser: it resolves to a
 * non-embedded environment instead of throwing, so the normal web app still works.
 */
export async function configureZoom(): Promise<ZoomEnvironment> {
  if (typeof window === 'undefined') {
    environment = NOT_EMBEDDED;
    return environment;
  }

  // There is no reliable way to detect the host before configuring: the desktop
  // client runs the app as a top-level webview while the Zoom Web App uses an
  // iframe. `config` succeeding is the definitive signal, and it rejects fast in a
  // normal browser tab.
  const holder = await loadSdk();
  if (!holder) {
    environment = NOT_EMBEDDED;
    return environment;
  }

  const { sdk: zoomSdk } = holder;

  try {
    // Race the bridge call: in a plain browser nothing answers, and waiting on the
    // SDK's internal timeout would block the first render.
    const response = await Promise.race([
      zoomSdk.config({
        capabilities: REQUESTED_CAPABILITIES,
        // Applies when the user pops the app out of the client into its own window.
        popoutSize: { width: 480, height: 720 },
        timeout: CONFIG_TIMEOUT_MS
      }),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('zoom config timed out')), CONFIG_TIMEOUT_MS)
      )
    ]);

    let supportedApis: Apis[] | undefined;
    try {
      supportedApis = (await zoomSdk.getSupportedJsApis()).supportedApis;
    } catch {
      supportedApis = undefined;
    }

    environment = buildEnvironment(response, supportedApis);
    return environment;
  } catch {
    environment = NOT_EMBEDDED;
    return environment;
  }
}

/**
 * Returns the encrypted Zoom app context. This is an opaque token that only the API
 * can decrypt (it needs the Zoom client secret), so the browser never interprets or
 * stores meeting identifiers itself.
 */
export async function getZoomAppContext(): Promise<string | null> {
  if (!environment.embedded || !environment.supports('getAppContext')) return null;

  const holder = await loadSdk();
  if (!holder) return null;

  try {
    return (await holder.sdk.getAppContext()).context;
  } catch {
    return null;
  }
}

export type ZoomUserContext = GetUserContextResponse;

/** Returns only the current user's meeting-scoped identity and role. */
export async function getZoomUserContext(): Promise<ZoomUserContext | null> {
  if (!environment.embedded || !environment.supports('getUserContext')) return null;

  const holder = await loadSdk();
  if (!holder) return null;

  try {
    return await holder.sdk.getUserContext();
  } catch {
    return null;
  }
}

/**
 * Broadcasts a small JSON payload to open instances of this app in the meeting.
 * Zoom caps the payload at 1 KB; enforce a conservative ceiling before crossing the
 * bridge so drawing chunks cannot accidentally trip SDK error 10229.
 */
export async function sendZoomMessage(payload: JSONObject): Promise<boolean> {
  if (!environment.embedded || !environment.inMeeting || !environment.supports('sendMessage')) {
    return false;
  }

  if (new TextEncoder().encode(JSON.stringify(payload)).byteLength > 950) {
    return false;
  }

  const holder = await loadSdk();
  if (!holder) return false;

  try {
    await holder.sdk.sendMessage({ payload });
    return true;
  } catch {
    return false;
  }
}

/** Registers one named listener and returns an idempotent teardown. */
export async function subscribeToZoomMessages(
  handler: (event: OnMessageEvent) => void
): Promise<() => void> {
  if (!environment.embedded || !environment.inMeeting || !environment.supports('onMessage')) {
    return () => {};
  }

  const holder = await loadSdk();
  if (!holder) return () => {};

  const listener = (event: OnMessageEvent) => handler(event);
  holder.sdk.addEventListener('onMessage', listener);

  return () => {
    try {
      holder.sdk.removeEventListener('onMessage', listener);
    } catch {
      // The Zoom webview may already be gone.
    }
  };
}

export type InvitationResult = 'sent' | 'dialog' | 'unsupported';

/**
 * Invites the other meeting participants to open the app. Falls back through the
 * available mechanisms and finally reports `unsupported` so the UI can show the
 * QR code and shareable link instead (the web client and non-hosts often land here).
 */
export async function inviteParticipants(): Promise<InvitationResult> {
  if (!environment.embedded || !environment.inMeeting) return 'unsupported';

  const holder = await loadSdk();
  if (!holder) return 'unsupported';

  const { sdk: zoomSdk } = holder;

  if (environment.supports('sendAppInvitationToAllParticipants')) {
    try {
      await zoomSdk.sendAppInvitationToAllParticipants();
      return 'sent';
    } catch {
      // Fall through to the participant picker.
    }
  }

  if (environment.supports('showAppInvitationDialog')) {
    try {
      await zoomSdk.showAppInvitationDialog({
        dialogHeading: 'Invite people to play Scribl',
        dialogButtonText: 'Invite',
        messageText: 'Join the Scribl game in this meeting.',
        primaryButtonText: 'Open',
        secondaryButtonText: 'Ignore'
      });
      return 'dialog';
    } catch {
      return 'unsupported';
    }
  }

  return 'unsupported';
}

type ZoomLifecycleHandlers = {
  onContextChange?: (environment: ZoomEnvironment) => void;
  onResume?: () => void;
};

/**
 * Zoom can suspend, reload, or move the app between contexts (for example when a
 * user starts a meeting from the main client, or the panel is reopened). Both
 * require reconfiguring the SDK and resyncing authoritative game state.
 */
export async function subscribeToZoomLifecycle(
  handlers: ZoomLifecycleHandlers
): Promise<() => void> {
  if (!environment.embedded) return () => {};

  const holder = await loadSdk();
  if (!holder) return () => {};

  const { sdk: zoomSdk } = holder;

  const handleRunningContextChange = async () => {
    const next = await configureZoom();
    handlers.onContextChange?.(next);
    handlers.onResume?.();
  };

  const handleUserContextChange = async () => {
    const next = await configureZoom();
    handlers.onContextChange?.(next);
  };

  const handleVisibilityChange = (event: { visible: boolean }) => {
    if (event?.visible) handlers.onResume?.();
  };

  try {
    zoomSdk.addEventListener('onRunningContextChange', handleRunningContextChange);
    zoomSdk.addEventListener('onMyUserContextChange', handleUserContextChange);
    zoomSdk.addEventListener('onAppVisibilityChange', handleVisibilityChange);
  } catch {
    return () => {};
  }

  return () => {
    try {
      zoomSdk.removeEventListener('onRunningContextChange', handleRunningContextChange);
      zoomSdk.removeEventListener('onMyUserContextChange', handleUserContextChange);
      zoomSdk.removeEventListener('onAppVisibilityChange', handleVisibilityChange);
    } catch {
      // The client tears listeners down with the webview; ignore removal failures.
    }
  };
}
